import type { FlowStage, RunStageOut } from '@/services/engineering';

import styles from './FlowTimeline.module.css';

export interface FlowTimelineProps {
  stages: FlowStage[];
  selectedId: string | null;
  onSelect: (stageId: string) => void;
  /** saída da última execução, por id de estágio */
  outputs: Record<string, RunStageOut>;
  /** estágio sendo executado agora (mostra o nó "a rodar") */
  runningId?: string | null;
}

/**
 * Linha do tempo HORIZONTAL do fluxo (plano §11.1).
 *
 * Todo nó aparece — os de código também. Esconder os nós determinísticos foi
 * o que fez a tela anterior parecer que o fluxo era só de IA, e ninguém via
 * onde a régua era aplicada. `kind` vem do backend; a tela não adivinha.
 */
export function FlowTimeline({
  stages,
  selectedId,
  onSelect,
  outputs,
  runningId,
}: FlowTimelineProps) {
  return (
    <ol className={styles.rail} aria-label="Estágios do fluxo, em ordem">
      {stages.map((s, i) => {
        const out = outputs[s.id];
        const rodando = runningId === s.id;
        const estado = rodando
          ? 'rodando'
          : !out
            ? 'parado'
            : out.ok
              ? 'ok'
              : 'erro';
        return (
          <li key={s.id} className={styles.item}>
            {i > 0 && <span className={styles.link} aria-hidden="true" />}
            <button
              type="button"
              className={[
                styles.node,
                s.kind === 'codigo' ? styles.nodeCodigo : styles.nodeIa,
                s.id === selectedId ? styles.nodeAtivo : '',
              ].join(' ')}
              onClick={() => onSelect(s.id)}
              aria-current={s.id === selectedId ? 'true' : undefined}
            >
              <span className={styles.topo}>
                <span className={styles.ordem}>{s.ordem}</span>
                <span
                  className={`${styles.estado} ${styles[`estado_${estado}`]}`}
                  title={
                    estado === 'ok'
                      ? 'Saída capturada na última execução'
                      : estado === 'erro'
                        ? 'Falhou na última execução'
                        : estado === 'rodando'
                          ? 'A rodar'
                          : 'Sem execução nesta sessão'
                  }
                />
              </span>
              <span className={styles.nome}>{s.nome}</span>
              <span className={styles.tipo}>
                {s.kind === 'codigo' ? 'código' : 'IA'}
              </span>
              <span className={styles.modelo}>
                {s.kind === 'codigo' ? 'determinístico' : s.model || 'modelo não informado'}
              </span>
              {s.is_overridden && <span className={styles.override}>Role editado</span>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
